import React, { useEffect, useState } from "react";
import { Alert, Button, Card, Col, Container, Form, Row, Spinner } from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../../index";

export type DeckData = {
    deck_id: number,
    deck_name: String,
    deleteDeck: Function,
    setShowCopiedToast: Function
}

export function DeckRow({ deck_id, deck_name, deleteDeck, editable = true, setShowCopiedToast }: DeckData & { editable?: boolean }) {
    return (
        <Col xs={12} md={6} lg={3} className="mb-4">
            <Card className="h-100 shadow-sm">
                <Card.Body className="d-flex flex-column">
                    <Card.Title className="mb-2" >
                        {deck_name}
                    </Card.Title>
                    <div className="mt-auto d-flex justify-content-between">
                        <Link to={`/decks/${deck_id}`} className="btn btn-primary btn-sm">
                            {editable ? "View / Edit" : "View"}
                        </Link>
                        <Link
                            to={""}
                            onClick={async () => {
                                setTimeout(() => setShowCopiedToast(false), 2000);
                                await api.post('/decks/copy', { deckId: deck_id }, { withCredentials: true });
                                setShowCopiedToast(true);
                            }}
                            style={{
                                position: "relative",
                                right: 0
                            }}
                            className="btn btn-outline-secondary btn-sm me-2"
                        >
                            Copy
                        </Link>
                        {editable ? <Button
                            variant="danger"
                            size="sm"
                            onClick={() => deleteDeck(deck_id)}
                        >
                            Delete
                        </Button> : ""}
                    </div>
                </Card.Body>
            </Card>
        </Col>
    );
}

export default function Decks() {
    const [deckList, setDeckList] = useState([]);
    const [deckName, setDeckName] = useState('');
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState<null | string>(null);
    const [showCopiedToast, setShowCopiedToast] = useState(false);

    const getDeckList = () => {
        if (!deckList)
            setLoading(true);
        api
            .post('/decks/get', {}, { withCredentials: true })
            .then((res) => {
                setDeckList(res.data);
                setErr(null);
            })
            .catch(() => {
                // Prefer a redirect via router if possible
                window.location.href = '/logout';
            })
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        getDeckList();
    }, [showCopiedToast]);

    const deleteDeck = (deck_id: number) => {
        api
            .post('/decks/delete', { deckId: deck_id }, { withCredentials: true })
            .then(() => {
                getDeckList();
            })
            .catch(() => {
                // Optional: show an error message
                setErr('Failed to delete deck.');
            });
    };

    const createDeck = () => {
        if (!deckName.trim()) return;
        api
            .post('/decks/add', { deckname: deckName }, { withCredentials: true })
            .then(() => {
                setDeckName('');
                getDeckList();
            })
            .catch(() => {
                setErr('Failed to create deck.');
            });
    };

    const deckCards = deckList.map((item: DeckData) => (
        <DeckRow
            key={item.deck_id}
            deck_id={item.deck_id}
            deck_name={item.deck_name}
            deleteDeck={deleteDeck}
            setShowCopiedToast={setShowCopiedToast}
        />
    ));

    return (
        <Container className="my-4 pt-2 pb-2">
            {showCopiedToast && (
                <div className="toast show position-fixed top-0 end-0 m-3" style={{ zIndex: 1050 }}>
                    <div className="toast-body">Copied</div>
                </div>
            )}
            <Row className="mb-3 align-items-center">
                <Col>
                    <h2 className="mb-0">Decks</h2>
                </Col>
                <Col className="text-end">
                    <Link to="/home" className="btn btn-outline-secondary btn-sm">
                        Back to Home
                    </Link>
                </Col>
            </Row>

            <Form
                onSubmit={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    createDeck();
                }}
            >
                <Row className="align-items-end g-2">
                    <Col xs={12} md={6} lg={4}>
                        <Form.Control
                            placeholder="New deck name"
                            value={deckName}
                            onChange={(e) => setDeckName(e.target.value)}
                        />
                    </Col>
                    <Col xs={12} md={4} lg={2}>
                        <Button type="submit" className="w-100" variant="primary">
                            Add Deck
                        </Button>
                    </Col>
                </Row>
            </Form>

            <hr className="my-4" />

            {loading ? (
                <Row className="justify-content-center">
                    <Spinner animation="border" />
                </Row>
            ) : err ? (
                <Alert variant="danger" className="my-3">
                    {err}
                </Alert>
            ) : deckCards.length === 0 ? (
                <Alert variant="info" className="my-3">
                    No decks found. Create one to get started.
                </Alert>
            ) : (
                <Row className="g-4">{deckCards}</Row>
            )}
        </Container>
    );
}
